import { useEffect, useRef } from "react";
import {
  GalleryData,
  GenProjectFormComponetProps,
  Links,
  ProjectDataProps,
  SelectOption,
} from "../../../common/types";
import { ErrorMessage, inputContainerStyle, InputStyled } from "../contactForm";
import Select, { SingleValue } from "react-select";
import {
  getCustomStylesSingleSelect,
  projectOptions,
} from "../../../../utils/CustomizedLibraries/CustomSelectInput";
import {
  handleProjectFormBlur,
  ProjectvalidateField,
} from "../../../../utils/Validations/adminFormValidationUtil";
import MuiDatePicker from "./MaterialDatePicker";
import { useTheme as styledTheme } from "styled-components";
import { useAuth } from "../../../../context/AuthContext";

const CombinedInput: React.FC<GenProjectFormComponetProps> = ({
  projectData,
  errors,
  touched,
  setProjectData,
  setErrors,
  setTouched,
}) => {
  const styledComponentTheme = styledTheme();
  const { fetchedProjects: projects } = useAuth();

  const handleBlurWrapper = (
    name: keyof ProjectDataProps,
    value:
      string
      | number
      | SingleValue<SelectOption>
      | GalleryData
      | Partial<Links>
  ) => {
    handleProjectFormBlur(
      name,
      value,
      touched,
      setTouched,
      validateFieldWrapper
    );
  };

  const validateFieldWrapper = (
    name: keyof ProjectDataProps,
    value:
      string
      | SingleValue<SelectOption>
      | number
      | GalleryData
      | Partial<Links>
  ) => {
    ProjectvalidateField(name, value, errors, setErrors, projectData, projects);
  };

  // Handle input changes
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    // For generic string inputs like name and description
    setProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // console.log("Input Value", value);
    validateFieldWrapper(name as keyof ProjectDataProps, value);
  };

  const typeRef = useRef(projectData.type);
  useEffect(() => {
    typeRef.current = projectData.type;
  }, [projectData.type]);
  const handleSelectChange = (selectedOption: SingleValue<SelectOption>) => {
    setProjectData((prev) => ({
      ...prev,
      type: selectedOption,
    }));
    typeRef.current = selectedOption; // Ensure the ref is updated synchronously
    if (touched["type"]) {
      // console.log("Input Value", value);
      validateFieldWrapper("type", selectedOption);
    }
  };

  return (
    <>
      <div style={inputContainerStyle}>
        <label htmlFor="projectname">Project name</label>
        <InputStyled
          type="text"
          id="projectname"
          autoComplete="name"
          name="name"
          placeholder="Enter project name"
          value={projectData.name}
          onChange={handleChange}
          onBlur={() => handleBlurWrapper("name", projectData.name)}
          $errorColor={errors.name ? "red" : ""}
        />
        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
      </div>

      <MuiDatePicker
        projectData={projectData}
        setProjectData={setProjectData}
        errors={errors}
        setErrors={setErrors}
        setTouched={setTouched}
        touched={touched}
      />

      <div style={inputContainerStyle}>
        <label htmlFor="type">Select Project type</label>
        <Select
          inputId="type"
          value={
            projectData.type && typeof projectData.type === "object"
              ? projectData.type
              : null
          }
          onChange={handleSelectChange}
          placeholder="Select project type"
          options={projectOptions}
          styles={getCustomStylesSingleSelect(styledComponentTheme, !!errors.type)}
          isSearchable={false}
          onBlur={() => handleBlurWrapper("type", typeRef.current)}
        />
        {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
      </div>

      <div style={inputContainerStyle}>
        <label htmlFor="description">Description</label>
        <InputStyled
          as="textarea"
          id="description"
          name="description"
          placeholder={`Give a description of your project`}
          value={projectData.description}
          onChange={handleChange}
          style={{ height: "150px" }}
          onBlur={() =>
            handleBlurWrapper("description", projectData.description)
          }
          $errorColor={errors.description ? "red" : ""}
        />
        {errors.description && (
          <ErrorMessage>{errors.description}</ErrorMessage>
        )}
      </div>
    </>
  );
};

export default CombinedInput;
